import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/auth-provider";
import { useGoogleMapsKey } from "@/hooks/useGoogleMapsKey";
import { GooglePlacesInput } from "./GooglePlacesInput";
import { useGooglePlacesService } from "@/hooks/address/useGooglePlacesService";
import { GooglePlacesSelectValue } from "@/types/google-places";

interface AddressStandaloneFormProps {
  initialData?: {
    street: string;
    street2: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  label?: string;
  addressId?: string;
  onSave: () => void;
  onCancel: () => void;
}

export const AddressStandaloneForm = ({
  initialData,
  label = '',
  addressId,
  onSave,
  onCancel,
}: AddressStandaloneFormProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { apiKey, error: apiError, isLoading: apiLoading } = useGoogleMapsKey();
  const { getPlaceDetails, extractAddressComponents } = useGooglePlacesService();
  const [saving, setSaving] = useState(false);
  const [showManualFields, setShowManualFields] = useState(!!initialData?.street);
  
  const [formData, setFormData] = useState({
    street: initialData?.street || '',
    street2: initialData?.street2 || '',
    city: initialData?.city || '',
    province: initialData?.province || '',
    postalCode: initialData?.postalCode || '',
    country: initialData?.country || 'South Africa',
    label: label,
  });

  const handlePlaceSelect = async (place: GooglePlacesSelectValue) => {
    try {
      const placeDetails = await getPlaceDetails(place.value.place_id);
      if (placeDetails.address_components) {
        const components = extractAddressComponents(placeDetails.address_components);
        const fullStreet = components.streetNumber 
          ? `${components.streetNumber} ${components.street}`.trim()
          : components.street;
        
        setFormData(prev => ({
          ...prev,
          street: fullStreet,
          street2: '',
          city: components.city || components.sublocality,
          province: components.province,
          postalCode: components.postalCode,
          country: components.country,
        }));
        setShowManualFields(true);
      }
    } catch (error) {
      console.error('Error extracting address components:', error);
      toast({
        title: "Error",
        description: "Failed to extract address details. Please fill in manually.",
        variant: "destructive",
      });
      setShowManualFields(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !formData.street.trim() || !formData.city.trim()) {
      toast({
        title: "Error",
        description: "Street and city are required.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      if (addressId) {
        // Update existing address
        const { error } = await supabase
          .from('address_book')
          .update({
            street: formData.street.trim(),
            street2: formData.street2.trim() || null,
            city: formData.city.trim(),
            province: formData.province.trim(),
            postal_code: formData.postalCode.trim(),
            country: formData.country.trim(),
            label: formData.label.trim() || null,
          })
          .eq('id', addressId);

        if (error) throw error;
      } else {
        // Create new address
        const { error } = await supabase
          .from('address_book')
          .insert({
            user_id: user.id,
            street: formData.street.trim(),
            street2: formData.street2.trim() || null,
            city: formData.city.trim(),
            province: formData.province.trim(),
            postal_code: formData.postalCode.trim(),
            country: formData.country.trim(),
            label: formData.label.trim() || null,
            is_saved: true,
          });

        if (error) throw error;
      }

      toast({
        title: addressId ? "Address updated" : "Address added",
        description: "Address has been saved to your address book.",
      });
      
      onSave();
    } catch (error) {
      console.error('Error saving address:', error);
      toast({
        title: "Error",
        description: "Failed to save address. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (apiLoading) {
    return <div className="flex items-center justify-center p-4">Loading...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Google Places Input */}
      {apiKey && !apiError && (
        <div className="space-y-2">
          <Label>Search for address</Label>
          <GooglePlacesInput
            apiKey={apiKey}
            placeholder="Start typing an address..."
            onSelect={handlePlaceSelect}
          />
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => setShowManualFields(true)}
          >
            Or enter manually
          </Button>
        </div>
      )}

      {/* Manual Fields */}
      {showManualFields && (
        <>
          <div className="space-y-2">
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              value={formData.label}
              onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
              placeholder="e.g., Home, Work, Office"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="street">Street Address *</Label>
            <Input
              id="street"
              value={formData.street}
              onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
              placeholder="123 Main Street"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="street2">Apartment, suite, etc.</Label>
            <Input
              id="street2"
              value={formData.street2}
              onChange={(e) => setFormData(prev => ({ ...prev, street2: e.target.value }))}
              placeholder="Apt 4B, Suite 100, etc."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Cape Town"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="province">Province</Label>
              <Input
                id="province"
                value={formData.province}
                onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
                placeholder="Western Cape"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="postalCode">Postal Code</Label>
              <Input
                id="postalCode"
                value={formData.postalCode}
                onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                placeholder="8001"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                placeholder="South Africa"
              />
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving...' : addressId ? 'Update Address' : 'Add Address'}
        </Button>
      </div>
    </form>
  );
};